import { z } from "zod";

import { apiGet, apiPost } from "../lib/api";

export const CallStateSchema = z.enum(["LIVE", "ENDED", "HALTED"]);
export const VerdictSchema = z.enum(["CLEAN", "REJECT", "FAILED"]).nullable();

export const CallListItemSchema = z.object({
  call_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  persona_id: z.string().uuid().nullable().optional(),
  target_id: z.string().uuid().nullable().optional(),
  state: CallStateSchema,
  started_at: z.string(),
  ended_at: z.string().nullable().optional(),
  turn_count: z.number().default(0),
  rejection_count: z.number().default(0),
  verdict: VerdictSchema.optional(),
  termination_reason: z.string().nullable().optional(),
  sip_trunk_label: z.string().nullable().optional(),
  has_recording: z.boolean().default(false),
  target_label: z.string().nullable().optional(),
  target_phone: z.string().nullable().optional(),
  language: z.string().nullable().optional(),
  livekit_room_name: z.string().nullable().optional(),
  voice_pipeline_mode: z.string().nullable().optional(),
  voice_id: z.string().nullable().optional(),
});
export type CallListItem = z.infer<typeof CallListItemSchema>;

export const CallListResponseSchema = z.object({
  items: z.array(CallListItemSchema),
  total: z.number(),
});

// Matches console_api.schemas.call.TurnListItem (shipped 2026-05-16).
// Each Turn is a Q→A pair: target spoke (user_text) and persona replied
// (bot_text) within one turn_index. latencies_ms / tokens / errors are
// stored as JSON blobs server-side; the API returns them as parsed
// objects/arrays.
export const TurnEntrySchema = z.object({
  turn_id: z.string(),
  turn_index: z.number(),
  started_at: z.string(),
  user_text: z.string().nullable().optional(),
  bot_text: z.string().nullable().optional(),
  models: z.record(z.string(), z.unknown()).default({}),
  latencies_ms: z.record(z.string(), z.number()).default({}),
  tokens: z.record(z.string(), z.number()).default({}),
  errors: z.array(z.record(z.string(), z.unknown())).default([]),
  // The API has returned this since 2026-07-28; zod was silently stripping it.
  // An interrupted (barge-in) turn's latencies are truncated, so pooling it
  // with normal turns skews any timing read off this data.
  interrupted: z.boolean().default(false),
});
export type TurnEntry = z.infer<typeof TurnEntrySchema>;

export const CallDetailResponseSchema = z.object({
  record: CallListItemSchema,
  turns: z.array(TurnEntrySchema),
  audit_chain_root_hash: z.string().nullable().optional(),
  last_audit_seq: z.number().nullable().optional(),
});
export type CallDetailResponse = z.infer<typeof CallDetailResponseSchema>;

const HaltAckSchema = z.object({ status: z.string() });

export function fetchCalls(opts: {
  state?: "LIVE" | "ENDED" | "HALTED";
  engagement_id?: string;
} = {}) {
  const parts: string[] = [];
  if (opts.state) parts.push(`state=${opts.state}`);
  if (opts.engagement_id) parts.push(`engagement_id=${opts.engagement_id}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return apiGet(`/api/calls${qs}`, CallListResponseSchema);
}

export function fetchCall(id: string) {
  return apiGet(`/api/calls/${id}`, CallDetailResponseSchema);
}

export function haltCall(id: string, reason: string) {
  return apiPost(`/api/calls/${id}/halt`, HaltAckSchema, { reason });
}

export const RecordingPlaybackResponseSchema = z.object({
  call_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  url: z.string().url(),
  expires_at: z.string(),
  ttl_seconds: z.number(),
});
export type RecordingPlaybackResponse = z.infer<typeof RecordingPlaybackResponseSchema>;

export const RecordingAccessEntrySchema = z.object({
  operator_email: z.string(),
  accessed_at: z.string(),
  ttl_seconds: z.number(),
});
export type RecordingAccessEntry = z.infer<typeof RecordingAccessEntrySchema>;

export const RecordingAccessListResponseSchema = z.object({
  items: z.array(RecordingAccessEntrySchema),
  total: z.number(),
});
export type RecordingAccessListResponse = z.infer<typeof RecordingAccessListResponseSchema>;

/**
 * URL the browser passes to <audio src=...>. Authenticated per request
 * via the session cookie (`crossOrigin="use-credentials"` on the audio
 * element). The server streams S3 bytes through console-api after
 * validating the cookie — no presigned URL is ever minted.
 *
 * When the session expires, the next range request 401s and playback
 * stops automatically. There's no in-flight URL to leak.
 */
export function buildRecordingStreamUrl(callId: string): string {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
  return `${base}/api/calls/${callId}/recording`;
}

export function fetchRecordingAccessLog(callId: string) {
  return apiGet(
    `/api/calls/${callId}/recording/audit`,
    RecordingAccessListResponseSchema,
  );
}
